import React, { useState, useEffect } from 'react';
import { hot } from 'react-hot-loader';
import { connect } from 'react-redux';
import Link from 'redux-first-router-link';
import Top from '../Top/Top';
import Price from './Price';
import Purchase from './Purchase';
import StructuredData from './StructuredData';
import viewAnalytics from './viewAnalytics';
import TemplateParser from '../Templates/TemplateParser';
import Category from '../Category/Category';
import ProductItem from '../Category/ProductItem';
import Slider from '../../hocs/Slider/Slider';
import ErrorBoundary from '../../hocs/ErrorBoundary/ErrorBoundary';
import Pagination from '../../hocs/Pagination/Pagination';
import { translateObjectDefaultData, translateObjectData } from '../../utils/locale';
import { translate, prefix, urlify, rootUrl, baseName, camelCase } from '../../utils/string';
import { purifier } from '../../utils/dom';
import { addRecentlyViewed, retrieveProductCache, listItemCache } from '../../actions/checkout';
import { toggleFavourite, goToProduct } from '../../actions/environment';
import { origin } from '../../config';
import facebook from '../../assets/facebook.svg';
import twitter from '../../assets/twitter.svg';
import pinterest from '../../assets/pinterest.svg';
import email from '../../assets/email.svg';
import s from './Product.scss';

const ProductPage = ({ data, currentPath, setup, checkout, favourites, brands, header, siteUpdated, productCache = {}, recentlyViewed, ...props }) => {
	if (!data?.productData || !productCache?.[setup.market]?.[setup.pricelist]) {
		props.retrieveProductCache();
		return null;
	}

	const [section, updateSection] = useState('description');
	const [shareOpen, toggleShare] = useState(false);
	const [wholeDescription, toggleDescription] = useState(false);
	const [openIncentives, updateIncentives] = useState([]);

	const product = translateObjectData(setup, data.productData);
	const price = product.markets[setup.market].pricesByPricelist[setup.pricelist];

	useEffect(() => {
		if (typeof window.dataLayer === 'undefined') window.dataLayer = [];
		props.addRecentlyViewed(product.product);
		viewAnalytics(price, product, setup, checkout.listItemCache || {});
	}, []);

	const sectionHandler = (e) => {
		e.preventDefault();
		const newSection = e.currentTarget.getAttribute('href').slice(1);

		updateSection(newSection);
		toggleDescription(false);
	};

	const wholeDescriptionHandler = (e) => {
		e.preventDefault();
		toggleDescription((prevState) => !prevState);
	};

	const openShareHandler = (e) => {
		e.preventDefault();

		toggleShare((prevState) => !prevState);
	};

	const expandIncentiveHandler = (e) => {
		e.preventDefault();
		const id = e.currentTarget.id;

		updateIncentives((prevState) => {
			const currentIndex = prevState.indexOf(id);
			return (currentIndex === -1 ? [...prevState, id] : [...prevState.slice(0, currentIndex), ...prevState.slice(currentIndex + 1)]);
		});
	};

	const addToFavouritesHandler = (e) => {
		e.preventDefault();
		const favouritesProductId = e.currentTarget.dataset.favouritesProductId;
		props.toggleFavourite(favouritesProductId);
	};

	const language = setup.language;
	const soldOut = product.markets[setup.market].stockOfAllItems === 0;
	const soldOutText = product.sold_out && product.sold_out[language] ? product.sold_out[language] : translate(setup, 'soldOut');
	const descriptionParts = product.description.split(/<br\s?\/>/);
	const sizes = Object.keys(product.items).map((item) => ({ name: product.items[item].name, item, stock: product.items[item].stockByMarket[setup.market] }));
	const shareLocation = `${origin}${prefix(setup, product.canonicalUri)}`;
	const shareDescription = `${product.name} — APLACE`;
	let incentiveCounter = 0;
	const productSections = data.parts || [];

	const images = product.media.length > 0 ? product.media.map((img) => img.sources.zoom.url) : [];
	const metaInfo = {
		title: `${product.name} - ${product.brandName}`,
		metaDescription: product.metaDescription,
		linkCanonical: shareLocation,
		images,
	};

	const productRelations = { standard: [], variant: [] };
	if (product?.relatedProducts) {
		product.relatedProducts.forEach((rP) => {
			const relationName = camelCase(rP.relation);
			if (!productRelations[relationName]) productRelations[relationName] = [];
			productRelations[relationName] = [...productRelations[relationName], rP.product];
		});
	}

	const cache = productCache[setup.market][setup.pricelist];
	const variants = [
		{ image: product.media.map((img) => img.sources.mini.url) || [], selected: true, name: product.name, uri: product.uri },
		// eslint-disable-next-line camelcase
		...productRelations.variant.filter((i) => {
			const relation = cache.products?.[i];

			if (!relation) return false;
			// remove out of stock items that are discontinued
			if (parseInt((relation.inStock || 0), 10) === 0) return false;
			return true;
		}).map((item) => (
			{
				image: cache.products?.[item]?.image || [], // eslint-disable-line camelcase
				uri: cache.products?.[item]?.uri,
				name: cache.products?.[item]?.name,
			}
		)),
	].sort((a, b) => a.name.localeCompare(b.name));

	const brandBaseUri = translateObjectDefaultData(setup, brands.posts[0].meta).fullUri;

	return (
		<main role="main">
			<Top setup={setup} pageContent={metaInfo} paths={data.paths} skipGeneral />
			<StructuredData canonical={shareLocation} product={product} soldOut={soldOut} price={price} currency={setup.countries[setup.country].currency} image={images} />
			<div className={s.product}>
				<ErrorBoundary message="Product images missing data" debugData={product}>
					<div className={s.productImages}>
						{
							product.media.length > 0 ? (
								<Slider adaption="product" infinite thumbs showNav={false}>
									{
										product.media.map((media) => (
											<div key={media.sources.zoom.url.slice(media.sources.zoom.url.lastIndexOf('/') + 1)} className={s.productImages_media}>
												<img className={s.productImages_media_image} src={media.sources.zoom.url} alt={`${product.brandName} ${product.name}`} />
											</div>
										))
									}
								</Slider>
							) : null
						}
					</div>
				</ErrorBoundary>
				<div className={s.productDetail}>

					<ErrorBoundary message="Product details missing data" debugData={product}>
						<div className={s.productDetail_top}>
							<span className={s.productDetail_top_price}><Price price={price} soldOut={soldOut ? soldOutText : false} /></span>
							<h2 className={s.productDetail_top_brand}>{product.brandName}</h2>
							<h1 className={s.productDetail_top_title}>{product.name}</h1>
						</div>

						<div className={s.productDetail_purchase}>
							<Purchase setup={setup} currentPath={currentPath} productId={data.productData.product} originalProduct={data.productData} sizes={sizes} instoreOnly={data.productData.instore_only} soldOut={soldOut ? soldOutText : false} />
						</div>

						<div className={s.productDetail_info}>
							<div className={s.productDetail_info_sections}>
								<a onClick={sectionHandler} className={[s.productDetail_info_sections_title, ...(section === 'description' ? [s.productDetail_info_sections_title__selected] : [])].join(' ')} href="#description">{ translate(setup, 'description') }</a>
								{ Object.keys(product.measurementChartRows).length > 0 ? <a onClick={sectionHandler} className={[s.productDetail_info_sections_title, ...(section === 'size-guide' ? [s.productDetail_info_sections_title__selected] : [])].join(' ')} href="#size-guide">{ translate(setup, 'sizeGuide') }</a> : null }
							</div>
							<div className={[s.productDetail_info_section, ...(section === 'description' ? [s.productDetail_info_section__selected] : [])].join(' ')}>
								<div className={[s.productDetail_info_section_inner, ...(wholeDescription ? [s.productDetail_info_section_inner__all] : [])].join(' ')}>
									<p className={s.productDetail_info_section_inner_top}>{ descriptionParts[0] }</p>
									<p className={s.productDetail_info_section_inner_bottom} dangerouslySetInnerHTML={{ __html: purifier(descriptionParts.slice(1).join('<br />')) }} />
									{ descriptionParts.length > 0 && !wholeDescription ? <a onClick={wholeDescriptionHandler} className={[s.productDetail_info_section_inner_all, 'a-productReadMore'].join(' ')} href="#read-more">{ translate(setup, 'readMore') }</a> : null }
								</div>
							</div>
							{
								Object.keys(product.measurementChartRows).length > 0 ? (
									<div className={[s.productDetail_info_section, ...(section === 'size-guide' ? [s.productDetail_info_section__selected] : [])].join(' ')}>
										<div className={s.productDetail_info_section_inner}>
											<table className={s.productDetail_info_section_table}>
												<thead>
													<tr>
														<th />
														{
															Object.keys(product.measurementChartRows).slice(0, 1).map((row) => (
																Object.keys(product.measurementChartRows[row]).map((key) => <th key={key}>{ key }</th>)
															))
														}
													</tr>
												</thead>
												<tbody>
													{
														Object.keys(product.measurementChartRows).map((row) => (
															<tr key={urlify(`${row}`)}>
																<td>{ row }</td>
																{
																	Object.keys(product.measurementChartRows[row]).map((key) => <td key={urlify(`${row}-${key}`)}>{ product.measurementChartRows[row][key] }</td>)
																}
															</tr>
														))
													}
												</tbody>
											</table>
										</div>
									</div>
								) : null
							}

							{
								variants.length > 1 && (
									<div className={s.productDetail_variants}>
										<h3 className={s.productDetail_variants_title}>{ translate(setup, 'productVariants') }</h3>
										<div className={s.productDetail_variants_items}>
											{
												variants.map((variant) => (
													<Link key={variant.uri} to={prefix(setup, `${currentPath.slice(0, currentPath.lastIndexOf('/'))}/${variant.uri}`)} className={[s.productDetail_variants_item, ...(variant.selected ? [s.productDetail_variants_item__selected] : [])].join(' ')} title={variant.name}>
														<img className={s.productDetail_variants_item_image} src={variant.image[0]} alt={variant.name} />
													</Link>
												))
											}
										</div>
									</div>
								)
							}

							<div className={s.productDetail_info_brandLink}>
								<span>{ translate(setup, 'seeMoreFromBrand') }</span> <Link className="a-productBrand" to={prefix(setup, `${brandBaseUri}/${translate(setup, data.brand.uri)}`)}>{ product.brandName }</Link>
							</div>
						</div>
					</ErrorBoundary>

					<div className={s.productDetail_share}>
						<div className={[s.productDetail_share_left, ...(shareOpen ? [s.productDetail_share_left__open] : [])].join(' ')}>
							<a href="#shareProduct" onClick={openShareHandler} className={[s.productDetail_share_left_text, 'a-productShare'].join(' ')}>
								<span>{ translate(setup, 'share') }</span>
								<svg xmlns="http://www.w3.org/2000/svg" width="19" height="23" viewBox="0 0 19 23">
									<g fill="none" fillRule="evenodd" stroke="#000" strokeWidth="1.3" transform="translate(0 1)">
										<path d="M.65 14.65L.65 21.35 18.35 21.35 18.35 14.65" />
										<path d="M9.43408203 16.6413574L9.43408203.528310441" />
										<polyline points="3.63 6.995 9.455 .171 15.008 6.995" />
									</g>
								</svg>
							</a>
							<div className={s.productDetail_share_left_inner}>
								<a title="Share on Facebook" rel="noopener noreferrer nofollow" target="_blank" href={`http://www.facebook.com/sharer.php?u=${encodeURIComponent(shareLocation)}`}><img src={facebook} alt="Facebook icon" /></a>
								<a title="Tweet" rel="noopener noreferrer nofollow" target="_blank" href={`http://twitter.com/intent/tweet?text=${encodeURIComponent(shareDescription)}&amp;url=${encodeURIComponent(shareLocation)}`}><img src={twitter} alt="Twitter icon" /></a>
								{ product.media.length > 0 ? <a title="Pin it on Pinterest" rel="noopener noreferrer nofollow" target="_blank" href={`http://pinterest.com/pin/create/button/?url=${encodeURIComponent(shareLocation)}&amp;media=${encodeURIComponent(product.media[0].sources.zoom.url)}&amp;description=${encodeURIComponent(shareDescription)}`}><img src={pinterest} alt="Pinterest icon" /></a> : null }
								<a href={`mailto:?subject=${encodeURIComponent(shareDescription)}&body=${encodeURIComponent(shareDescription)}%0A%0D${encodeURIComponent(shareLocation)}`}><img src={email} alt="Email icon" /></a>
							</div>
						</div>
						<a href="#addToFavourites" data-product-id={product.product} onClick={addToFavouritesHandler} className={[s.productDetail_share_right, 'a-productFavorite'].join(' ')}>
							<span className={s.productDetail_share_right_text}>{ translate(setup, 'addToFavourites') }</span>
							<svg xmlns="http://www.w3.org/2000/svg" width="24" height="22" viewBox="0 0 24 22">
								<path fill={favourites.indexOf(product.product) === -1 ? 'none' : '#A7C4EA'} stroke="#000" strokeWidth="1.3" d="M82.3607178,44.9685735 C89.1964111,38.2322454 85.5537109,32.7345647 81.5004883,32.0919866 C78.7983398,31.6636012 76.7208802,32.7314723 75.0893698,35.2955999 C73.4578594,32.7314723 71.3803998,31.6636012 68.6782514,32.0919866 C64.6250287,32.7345647 60.9823285,38.2322454 67.8180219,44.9685735 C70.9248433,48.0302334 73.289046,50.256918 75.0893698,51.6486272 C76.8896936,50.256918 79.2538964,48.0302334 82.3607178,44.9685735 Z" transform="translate(-63 -31)" />
							</svg>
						</a>
					</div>

					<ErrorBoundary message="Product page incentives missing data" debugData={product}>
						<div className={s.productDetail_incentives}>
							{
								header.incentives ? header.incentives.map((incentive) => {
									if (incentive.countryRestrict && incentive.countryRestrict.indexOf(setup.country) === -1) return null;
									return (
										incentive.texts.filter((incentiveSection) => incentiveSection.title && incentiveSection.content).map((incentiveSection) => {
											incentiveCounter += 1;
											return (
												<div key={urlify(translate(setup, incentiveSection.title).slice(0, 20))} className={[s.productDetail_incentives_section, ...(openIncentives.indexOf(`incentive_${incentiveCounter}`) !== -1 ? [s.productDetail_incentives_section__open] : [])].join(' ')}>
													<a id={`incentive_${incentiveCounter}`} className={s.productDetail_incentives_section_title} href="#openIncentive" onClick={expandIncentiveHandler}><span className="a-productIncentive">{ translate(setup, incentiveSection.title) }</span></a>
													<div className={s.productDetail_incentives_section_inner} dangerouslySetInnerHTML={{ __html: purifier(translate(setup, incentiveSection.content)) }} />
												</div>
											);
										})
									);
								}) : null
							}
						</div>
					</ErrorBoundary>
				</div>
			</div>
			{
				recentlyViewed && recentlyViewed.length > 0 && (
					<ErrorBoundary message="Recently Viewed Product details missing data" debugData={recentlyViewed}>
						<div className={s.productRelations}>
							<h2 className={s.productRelations_title}>{ translate(setup, 'recentlyViewedProducts') }</h2>
							<div className={s.productRelations_inner}>
								<Pagination ListComponent={ProductItem} list={recentlyViewed} cache={productCache[setup.market][setup.pricelist].products} currentPath="" canonical />
							</div>
						</div>
					</ErrorBoundary>
				)
			}
			{
				productSections.length > 0 && (
					<div className={s.productCMS}>
						<ErrorBoundary message="Product content incorrect format" debugData={productSections}>
							{
								productSections.map((pS) => (
									<TemplateParser sections={pS.sections} setup={setup} product={product} isProduct={product.product} />
								))
							}
						</ErrorBoundary>
					</div>
				)
			}
			{
				siteUpdated && (
					<ErrorBoundary message="Product page filter error">
						<Category data={data} currentPath={rootUrl(currentPath)} stateKey={baseName(currentPath)} />
					</ErrorBoundary>
				)
			}
		</main>
	);
};

const mapState = ({ environment, productCache, checkout }, { data, currentPath }) => ({
	data,
	currentPath,
	header: environment.header,
	setup: environment.setup,
	favourites: environment.favourites,
	brands: environment.brands,
	siteUpdated: environment.properties.siteUpdated,
	productCache,
	recentlyViewed: checkout.recentlyViewed,
	checkout,
});

export default hot(module)(connect(mapState, { toggleFavourite, listItemCache, addRecentlyViewed, retrieveProductCache, goToProduct })(ProductPage));
